import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { documentReviewAPI, auditLogAPI } from '../../services/api';
import { AuditLog } from '../../types';

interface DocumentTemplate {
  id: string;
  title: string;
  description?: string;
  type: 'text' | 'pdf' | 'html' | 'markdown';
  content?: string;
  filePath?: string;
  fileName?: string;
  displayOrder: number;
  isActive: boolean;
  isRequired: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateTemplateData {
  title: string;
  description: string;
  type: 'text' | 'pdf' | 'html' | 'markdown';
  content: string;
  displayOrder: number;
  isActive: boolean;
  isRequired: boolean;
}

export const DocumentTemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateTemplateData>({
    title: '',
    description: '',
    type: 'text',
    content: '',
    displayOrder: 0,
    isActive: true,
    isRequired: true
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Re-acceptance impact warning
  const [reacceptWarningOpen, setReacceptWarningOpen] = useState(false);
  const [checkingImpact, setCheckingImpact] = useState<'create' | 'update' | 'delete' | null>(null);
  const pendingSaveRef = useRef<(() => void) | null>(null);

  // Document history (audit log) — shown collapsed inside the Edit dialog
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState<string | null>(null);
  const [auditLogsPagination, setAuditLogsPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await documentReviewAPI.getTemplates();
      setTemplates(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'text',
      content: '',
      displayOrder: 0,
      isActive: true,
      isRequired: true
    });
    setUploadFile(null);
  };

  const handleCreateTemplate = async () => {
    try {
      setError(null);
      await documentReviewAPI.createTemplate(formData);
      setSuccess('Template created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadTemplates();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to create template');
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formDataUpload = new FormData();
      formDataUpload.append('document', uploadFile);
      formDataUpload.append('title', formData.title);
      formDataUpload.append('description', formData.description);
      formDataUpload.append('displayOrder', formData.displayOrder.toString());
      formDataUpload.append('isActive', formData.isActive.toString());
      formDataUpload.append('isRequired', formData.isRequired.toString());

      await documentReviewAPI.uploadTemplate(formDataUpload);
      setSuccess('Template uploaded successfully');
      setUploadDialogOpen(false);
      resetForm();
      loadTemplates();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const handleEditTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setError(null);
      await documentReviewAPI.updateTemplate(selectedTemplate.id, formData);
      setSuccess('Template updated successfully');
      setEditDialogOpen(false);
      resetForm();
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setError(null);
      await documentReviewAPI.deleteTemplate(selectedTemplate.id);
      setSuccess('Template deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedTemplate(null);
      loadTemplates();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete template');
    }
  };

  // Checks (server-side) whether the given create/update/delete will require
  // existing members to re-accept the document. If it will, shows the warning
  // dialog and defers `proceed` until the admin clicks "Proceed". Otherwise
  // `proceed` runs immediately.
  const runWithReacceptanceCheck = async (
    action: 'create' | 'update' | 'delete',
    payload: { id?: string; data?: any },
    proceed: () => void
  ) => {
    try {
      setCheckingImpact(action);
      setError(null);
      const response = await documentReviewAPI.checkReacceptanceImpact({ action, ...payload });
      if (response.data?.willInvalidate) {
        pendingSaveRef.current = proceed;
        setReacceptWarningOpen(true);
      } else {
        proceed();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to check document impact before saving');
    } finally {
      setCheckingImpact(null);
    }
  };

  const handleProceedWithSave = () => {
    setReacceptWarningOpen(false);
    const proceed = pendingSaveRef.current;
    pendingSaveRef.current = null;
    proceed?.();
  };

  const handleCancelReacceptWarning = () => {
    setReacceptWarningOpen(false);
    pendingSaveRef.current = null;
  };

  const fetchDocumentHistory = async (templateId: string, page: number = 1) => {
    setAuditLogsLoading(true);
    setAuditLogsError(null);
    try {
      const response = await auditLogAPI.getEntityAuditLogs('DocumentTemplate', templateId, {
        page,
        limit: auditLogsPagination.limit
      });
      setAuditLogs(response.data.data || []);
      setAuditLogsPagination(prev => ({
        ...prev,
        page,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0
      }));
    } catch (error: any) {
      console.error('Error fetching document history:', error);
      setAuditLogsError('Failed to load document history. You may not have permission to view this information.');
    } finally {
      setAuditLogsLoading(false);
    }
  };

  const handleHistoryAccordionChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setHistoryExpanded(isExpanded);
    if (isExpanded && selectedTemplate && auditLogs.length === 0 && !auditLogsLoading) {
      fetchDocumentHistory(selectedTemplate.id, 1);
    }
  };

  const handleHistoryPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    if (selectedTemplate) {
      fetchDocumentHistory(selectedTemplate.id, page);
    }
  };

  const getHistoryActionColor = (action: string): 'default' | 'success' | 'info' | 'error' | 'warning' => {
    switch (action) {
      case 'create': return 'success';
      case 'update': return 'info';
      case 'delete': return 'error';
      default: return 'default';
    }
  };

  const getHistoryChangedFieldsDisplay = (log: AuditLog): string => {
    if (!log.changedFields || log.changedFields.length === 0) {
      return log.description || 'No specific fields tracked';
    }

    const changes = log.changedFields.map(field => {
      const oldValue = log.previousValues?.[field];
      const newValue = log.newValues?.[field];
      const displayField = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

      if (oldValue !== undefined && newValue !== undefined) {
        return `${displayField}: "${oldValue}" → "${newValue}"`;
      } else if (newValue !== undefined) {
        return `${displayField}: Added "${newValue}"`;
      } else if (oldValue !== undefined) {
        return `${displayField}: Removed "${oldValue}"`;
      }
      return `${displayField}: changed`;
    });

    return changes.join(', ');
  };

  const openEditDialog = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      description: template.description || '',
      type: template.type,
      content: template.content || '',
      displayOrder: template.displayOrder,
      isActive: template.isActive,
      isRequired: template.isRequired
    });
    // Reset history panel state for the newly-opened document
    setHistoryExpanded(false);
    setAuditLogs([]);
    setAuditLogsError(null);
    setAuditLogsPagination({ page: 1, limit: 10, total: 0, pages: 0 });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    setDeleteConfirmOpen(true);
  };

  const handleDownloadDocument = async (template: DocumentTemplate) => {
    try {
      setError(null);
      const response = await documentReviewAPI.downloadDocument(template.id);

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = template.fileName || `${template.title}.${template.type === 'pdf' ? 'pdf' : 'txt'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to download document');
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      text: 'default',
      pdf: 'error',
      html: 'info',
      markdown: 'secondary'
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Documents
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => {
              resetForm();
              setUploadDialogOpen(true);
            }}
          >
            Upload File
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setCreateDialogOpen(true);
            }}
          >
            Create Template
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell>
                  <Box>
                    {template.type === 'pdf' ? (<Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                      onClick={() => handleDownloadDocument(template)}
                    >
                      {template.title}
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    </Typography>) : (<Typography
                      variant="body2"
                      fontWeight="medium"
                    >
                      {template.title}
                    </Typography>)}
                    {template.description && (
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {template.type.toUpperCase()}
                  {/* <Chip
                    label={template.type.toUpperCase()}
                    size="small"
                    color={getTypeColor(template.type)}
                  /> */}
                </TableCell>
                <TableCell>{template.displayOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={template.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={template.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={template.isRequired ? 'Required' : 'Optional'}
                    size="small"
                    color={template.isRequired ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(template)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(template)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Template Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Document Template</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                label="Type"
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="html">HTML</MenuItem>
                <MenuItem value="markdown">Markdown</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              fullWidth
              multiline
              rows={6}
              required
            />
            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => {
                      const isActive = e.target.checked;
                      setFormData({
                        ...formData,
                        isActive,
                        // If deactivating, also set isRequired to false
                        isRequired: isActive ? formData.isRequired : false
                      });
                    }}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    disabled={!formData.isActive}
                  />
                }
                label="Required"
              />
            </Box>
            {!formData.isActive && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Inactive documents cannot be marked as required
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={checkingImpact === 'create'}
            onClick={() => runWithReacceptanceCheck('create', { data: formData }, handleCreateTemplate)}
          >
            {checkingImpact === 'create' ? 'Checking...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Template Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Document Template</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Box>
              <input
                type="file"
                accept=".pdf,.txt,.html,.htm,.md,.doc,.docx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button variant="outlined" component="span" fullWidth>
                  {uploadFile ? uploadFile.name : 'Choose File'}
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Supported formats: PDF, TXT, HTML, Markdown, Word documents
              </Typography>
            </Box>
            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => {
                      const isActive = e.target.checked;
                      setFormData({
                        ...formData,
                        isActive,
                        // If deactivating, also set isRequired to false
                        isRequired: isActive ? formData.isRequired : false
                      });
                    }}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    disabled={!formData.isActive}
                  />
                }
                label="Required"
              />
            </Box>
            {!formData.isActive && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Inactive documents cannot be marked as required
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => runWithReacceptanceCheck('create', { data: formData }, handleUploadTemplate)}
            disabled={uploading || checkingImpact === 'create' || !uploadFile || !formData.title}
          >
            {checkingImpact === 'create' ? 'Checking...' : uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Document Template</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            {selectedTemplate?.type !== 'pdf' && (
              <TextField
                label="Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                fullWidth
                multiline
                rows={6}
                helperText="Note: Content editing is not available for PDF files"
              />
            )}
            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <Box display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => {
                      const isActive = e.target.checked;
                      setFormData({
                        ...formData,
                        isActive,
                        // If deactivating, also set isRequired to false
                        isRequired: isActive ? formData.isRequired : false
                      });
                    }}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    disabled={!formData.isActive}
                  />
                }
                label="Required"
              />
            </Box>
            {!formData.isActive && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Inactive documents cannot be marked as required
              </Alert>
            )}

            {/* History — collapsed by default, shows this document's audit trail */}
            {selectedTemplate && (
              <Accordion
                expanded={historyExpanded}
                onChange={handleHistoryAccordionChange}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: 'divider' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <HistoryIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight="medium">
                      History
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {auditLogsLoading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : auditLogsError ? (
                    <Alert severity="warning" sx={{ m: 2 }}>
                      {auditLogsError}
                    </Alert>
                  ) : auditLogs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                      No history records found for this document.
                    </Typography>
                  ) : (
                    <>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date &amp; Time</TableCell>
                              <TableCell>Action</TableCell>
                              <TableCell>Changes</TableCell>
                              <TableCell>User</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {auditLogs.map((log) => (
                              <TableRow key={log.id}>
                                <TableCell>
                                  <Typography variant="body2">
                                    {new Date(log.createdAt).toLocaleDateString()}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(log.createdAt).toLocaleTimeString()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={String(log.action).replace('_', ' ').toUpperCase()}
                                    size="small"
                                    color={getHistoryActionColor(String(log.action))}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {getHistoryChangedFieldsDisplay(log)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {log.userFullName || 'Unknown'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {log.userEmail}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {auditLogsPagination.pages > 1 && (
                        <Box display="flex" justifyContent="center" p={2}>
                          <Pagination
                            count={auditLogsPagination.pages}
                            page={auditLogsPagination.page}
                            onChange={handleHistoryPageChange}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={checkingImpact === 'update'}
            onClick={() => selectedTemplate && runWithReacceptanceCheck(
              'update',
              { id: selectedTemplate.id, data: formData },
              handleEditTemplate
            )}
          >
            {checkingImpact === 'update' ? 'Checking...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{selectedTemplate?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={checkingImpact === 'delete'}
            onClick={() => {
              if (!selectedTemplate) return;
              setDeleteConfirmOpen(false);
              runWithReacceptanceCheck('delete', { id: selectedTemplate.id }, handleDeleteTemplate);
            }}
          >
            {checkingImpact === 'delete' ? 'Checking...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-acceptance Impact Warning Dialog */}
      <Dialog
        open={reacceptWarningOpen}
        onClose={handleCancelReacceptWarning}
      >
        <DialogTitle>Members will need to re-accept this document</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Saving this change will require existing members who have already accepted this
            document to review and accept it again.
          </Alert>
          <Typography variant="body2">
            Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReacceptWarning}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleProceedWithSave}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};